import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import EventIcon from '@mui/icons-material/Event';
import type { LabelDto, ProjectMemberDto, TaskSummaryDto } from '@taqon/contracts';
import { daysBetween } from '../../../shared/documents/dates';
import { PersonAvatar } from '../../../shared/ui/person';
import { PRIORITY_COLOURS, PRIORITY_LABELS } from './api';
import { remaining } from './my-tasks';

export function PriorityChip({ priority }: { priority: TaskSummaryDto['priority'] }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      color={PRIORITY_COLOURS[priority]}
      label={PRIORITY_LABELS[priority]}
    />
  );
}

/** A label as a chip tinted with its colour; the name is always written. */
export function LabelChip({ label }: { label: LabelDto }) {
  return (
    <Chip
      size="small"
      label={label.name}
      sx={{
        bgcolor: 'background.paper',
        border: 1,
        borderColor: label.colour,
        borderLeftWidth: 6,
        color: 'text.primary',
      }}
    />
  );
}

/**
 * The due date, red when the task is open and overdue, with the time remaining as its tooltip.
 * The date and "n days overdue" never break inside; in a narrow column the second goes below.
 */
export function DueDate({ task, today }: { task: TaskSummaryDto; today: string }) {
  if (task.dueDate === null) return null;
  const open = task.category !== 'done';
  const overdue = open && daysBetween(today, task.dueDate) < 0;
  const left = open ? remaining(task.dueDate, today) : 'Done';
  return (
    <Tooltip title={left ?? ''}>
      <Box
        component="span"
        sx={{
          display: 'inline-flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          columnGap: 0.75,
          fontSize: '0.8125rem',
          fontWeight: overdue ? 700 : 500,
          color: overdue ? 'error.main' : 'text.secondary',
        }}
      >
        <Box
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}
        >
          <EventIcon sx={{ fontSize: 16 }} aria-hidden />
          {task.dueDate}
        </Box>
        {overdue && (
          <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
            {left}
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

/** The assignees' photos, overlapping; their names are read out. */
export function Assignees({
  ids,
  members,
  size = 24,
}: {
  ids: readonly string[];
  members: readonly ProjectMemberDto[];
  size?: number;
}) {
  if (ids.length === 0) return null;
  const people = ids.map(
    (id) => members.find((m) => m.employeeId === id) ?? { employeeId: id, name: 'Former member' },
  );
  const names = people.map((p) => p.name).join(', ');
  return (
    <Tooltip title={names}>
      <Stack
        direction="row"
        aria-label={`Assigned to ${names}`}
        sx={{ '& > *:not(:first-of-type)': { ml: -0.75 } }}
      >
        {people.slice(0, 4).map((person) => (
          <Box
            key={person.employeeId}
            sx={{ border: 2, borderColor: 'background.paper', borderRadius: '50%', lineHeight: 0 }}
          >
            <PersonAvatar name={person.name} employeeId={person.employeeId} size={size} />
          </Box>
        ))}
        {people.length > 4 && (
          <Typography variant="caption" sx={{ ml: 0.5, alignSelf: 'center' }}>
            +{people.length - 4}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}

/** Checklist, subtask and comment counts, shown only when there is something to count. */
export function Progress({ task }: { task: TaskSummaryDto }) {
  const items: [string, ReactNode, string][] = [];
  if (task.subtasks.total > 0) {
    items.push([
      'subtasks',
      <AccountTreeOutlinedIcon key="s" sx={{ fontSize: 16 }} aria-hidden />,
      `${String(task.subtasks.done)}/${String(task.subtasks.total)}`,
    ]);
  }
  if (task.checklist.total > 0) {
    items.push([
      'checklist',
      <ChecklistIcon key="c" sx={{ fontSize: 16 }} aria-hidden />,
      `${String(task.checklist.done)}/${String(task.checklist.total)}`,
    ]);
  }
  if (task.comments > 0) {
    items.push([
      'comments',
      <ChatBubbleOutlineIcon key="m" sx={{ fontSize: 16 }} aria-hidden />,
      String(task.comments),
    ]);
  }
  if (items.length === 0) return null;
  return (
    <Stack direction="row" spacing={1.25} sx={{ color: 'text.secondary' }}>
      {items.map(([name, icon, text]) => (
        <Box
          key={name}
          component="span"
          aria-label={`${name} ${text}`}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, fontSize: '0.8125rem' }}
        >
          {icon}
          {text}
        </Box>
      ))}
    </Stack>
  );
}
